<div class="dashboard-page">
    <header class="dashboard-header">
        <div class="dashboard-header-text">
            <h1 class="dashboard-title">Dashboard</h1>
            <p class="dashboard-subtitle" *ngIf="overview?.von && overview?.bis">
                {{ overview.von | date: 'dd.MM.yyyy' }} – {{ overview.bis | date: 'dd.MM.yyyy' }}
            </p>
        </div>
        <div class="dashboard-toolbar">
            <mat-form-field
                class="dashboard-betrieb-picker"
                [class.has-value]="!!selectedBetriebId"
                (click)="openBetriebDialog()"
            >
                <mat-label>Betrieb</mat-label>
                <input matInput readonly [value]="selectedBetriebLabel" />
                <button
                    matSuffix
                    mat-icon-button
                    type="button"
                    color="accent"
                    class="dashboard-betrieb-clear"
                    [class.is-hidden]="!selectedBetriebId"
                    [disabled]="!selectedBetriebId"
                    matTooltip="Betrieb-Filter entfernen"
                    (click)="clearBetriebFilter(); $event.stopPropagation()"
                >
                    <mat-icon>cancel</mat-icon>
                </button>
            </mat-form-field>
            <kiene-zeitauswahl
                [hideAggregate]="true"
                [hideTag]="true"
                [disableFutureDates]="true"
                (change)="zeitintervalChanged($event)"
            ></kiene-zeitauswahl>
        </div>
    </header>

    <div *ngIf="loading" class="dashboard-loading">
        <mat-spinner diameter="40"></mat-spinner>
    </div>

    <div *ngIf="!loading" class="dashboard-content">
        <div class="dashboard-kpi-grid" *ngIf="kpiWidgets.length">
            <article class="dashboard-kpi" *ngFor="let w of kpiWidgets" [attr.data-id]="w.id">
                <div class="dashboard-kpi-top">
                    <span class="dashboard-kpi-icon" aria-hidden="true">
                        <mat-icon>{{ iconFor(w.id) }}</mat-icon>
                    </span>
                    <span class="dashboard-kpi-label">{{ w.title }}</span>
                </div>
                <p class="dashboard-kpi-value">{{ w.value | number: '1.0-0' }}</p>
                <p class="dashboard-kpi-hint" *ngIf="w.id === 'nutzer_online' && w.window_minutes">
                    Aktivität in den letzten {{ w.window_minutes }} Min.
                </p>
            </article>
        </div>

        <section class="dashboard-chart-card" *ngIf="overview?.series">
            <div class="dashboard-chart-toolbar">
                <mat-form-field class="dashboard-series-select" appearance="outline">
                    <mat-label>Anzeigen</mat-label>
                    <mat-select
                        [formControl]="seriesFilterCtrl"
                        multiple
                        (selectionChange)="onSeriesFilterChange($event)"
                    >
                        <mat-option *ngFor="let m of metricOptions" [value]="m.id">
                            {{ m.label }}
                        </mat-option>
                    </mat-select>
                </mat-form-field>
            </div>

            <div class="dashboard-selected-totals" *ngIf="selectedTotals.length">
                <div
                    class="dashboard-selected-total"
                    *ngFor="let t of selectedTotals; let i = index"
                    [class.has-divider]="i > 0"
                >
                    <div class="dashboard-selected-total-label">
                        <span
                            class="dashboard-selected-icon"
                            [style.--series-color]="t.color"
                            aria-hidden="true"
                        >
                            <mat-icon>{{ t.icon }}</mat-icon>
                        </span>
                        {{ t.label }}
                    </div>
                    <div class="dashboard-selected-total-value">{{ t.total | number: '1.0-0' }}</div>
                    <div
                        class="dashboard-selected-total-delta"
                        [class.is-up]="t.delta > 0"
                        [class.is-down]="t.delta < 0"
                    >
                        <span *ngIf="t.delta > 0">+</span>{{ t.delta | number: '1.0-0' }}
                        <ng-container *ngIf="t.deltaPercent !== null">
                            / <span *ngIf="t.deltaPercent > 0">+</span>{{ t.deltaPercent | number: '1.0-1' }}%
                        </ng-container>
                        <span class="dashboard-selected-total-vs">
                            vs
                            <ng-container *ngIf="overview?.prev_von && overview?.prev_bis; else prevFallback">
                                {{ overview.prev_von | date: 'dd.MM.' }}–{{ overview.prev_bis | date: 'dd.MM.yyyy' }}
                            </ng-container>
                            <ng-template #prevFallback>vorheriger Zeitraum</ng-template>
                        </span>
                    </div>
                </div>
            </div>

            <div
                class="dashboard-chart-wrap"
                (mousemove)="onAnimalChartMouseMove($event)"
                (mouseleave)="onAnimalChartMouseLeave($event)"
            >
                <div
                    class="dashboard-chart-cursor"
                    *ngIf="animalCursor"
                    [style.left.px]="animalCursor.x"
                    [style.top.px]="animalCursor.top"
                    [style.height.px]="animalCursor.height"
                ></div>
                <google-chart
                    class="dashboard-chart"
                    style="width: 100%"
                    [type]="chartType"
                    [columns]="chartColumns"
                    [data]="chartData"
                    [options]="chartOptions"
                    [height]="360"
                    [dynamicResize]="true"
                    (ready)="onAnimalChartReady($event)"
                >
                </google-chart>
            </div>
        </section>

        <section
            class="dashboard-chart-card"
            *ngIf="seriesMap.dau || seriesMap.sessions || seriesMap.hit_melder || seriesMap.new_users || seriesMap.new_betriebe"
        >
            <h2 class="dashboard-chart-title">Nutzer &amp; Betriebe (täglich)</h2>
            <p class="dashboard-chart-hint">
                Aktive Nutzer = eindeutige Konten. Sessions = Anmeldungen (oft höher bei geteilten Konten). HIT-Melder
                = Benutzer mit mindestens einer erfolgreichen Meldung an HI-Tier (jeder Benutzer im Zeitraum einmal).
                Neue Nutzer / Neue Betriebe = Neuanlagen an dem Tag.
            </p>

            <div class="dashboard-chart-toolbar">
                <mat-form-field class="dashboard-series-select" appearance="outline">
                    <mat-label>Anzeigen</mat-label>
                    <mat-select
                        [formControl]="userSeriesFilterCtrl"
                        multiple
                        (selectionChange)="onUserSeriesFilterChange($event)"
                    >
                        <mat-option *ngFor="let m of userMetricOptions" [value]="m.id">
                            {{ m.label }}
                        </mat-option>
                    </mat-select>
                </mat-form-field>
            </div>

            <div class="dashboard-selected-totals" *ngIf="userTotals.length">
                <div
                    class="dashboard-selected-total"
                    *ngFor="let t of userTotals; let i = index"
                    [class.has-divider]="i > 0"
                >
                    <div class="dashboard-selected-total-label">
                        <span
                            class="dashboard-selected-icon"
                            [style.--series-color]="t.color"
                            aria-hidden="true"
                        >
                            <mat-icon>{{ t.icon }}</mat-icon>
                        </span>
                        {{ t.label }} (Zeitraum)
                    </div>
                    <div class="dashboard-selected-total-value">{{ t.total | number: '1.0-0' }}</div>
                    <div
                        class="dashboard-selected-total-delta"
                        [class.is-up]="t.delta > 0"
                        [class.is-down]="t.delta < 0"
                    >
                        <span *ngIf="t.delta > 0">+</span>{{ t.delta | number: '1.0-0' }}
                        <ng-container *ngIf="t.deltaPercent !== null">
                            / <span *ngIf="t.deltaPercent > 0">+</span>{{ t.deltaPercent | number: '1.0-1' }}%
                        </ng-container>
                        <span class="dashboard-selected-total-vs">
                            vs
                            <ng-container *ngIf="overview?.prev_von && overview?.prev_bis; else dauPrevFallback">
                                {{ overview.prev_von | date: 'dd.MM.' }}–{{ overview.prev_bis | date: 'dd.MM.yyyy' }}
                            </ng-container>
                        </span>
                    </div>
                </div>
                <ng-template #dauPrevFallback>vorheriger Zeitraum</ng-template>
            </div>

            <div
                class="dashboard-chart-wrap"
                (mousemove)="onUserChartMouseMove($event)"
                (mouseleave)="onUserChartMouseLeave($event)"
            >
                <div
                    class="dashboard-chart-cursor"
                    *ngIf="userCursor"
                    [style.left.px]="userCursor.x"
                    [style.top.px]="userCursor.top"
                    [style.height.px]="userCursor.height"
                ></div>
                <google-chart
                    class="dashboard-chart"
                    style="width: 100%"
                    [type]="chartType"
                    [columns]="dauChartColumns"
                    [data]="dauChartData"
                    [options]="dauChartOptions"
                    [height]="300"
                    [dynamicResize]="true"
                    (ready)="onUserChartReady($event)"
                >
                </google-chart>
            </div>

            <div class="dashboard-new-users">
                <h3 class="dashboard-new-users-title">Neue Nutzer</h3>
                <p class="dashboard-chart-hint" *ngIf="!newUsersList.length">
                    Keine Neuanlagen im gewählten Zeitraum.
                </p>
                <ng-container *ngIf="newUsersList.length">
                    <p class="dashboard-chart-hint">
                        {{ newUsersList.length }} Neuanlage<span *ngIf="newUsersList.length !== 1">n</span> im gewählten
                        Zeitraum
                        <ng-container *ngIf="newUsersList.length >= 500"> (max. 500 angezeigt)</ng-container>.
                    </p>
                    <div class="dashboard-new-users-table-wrap">
                        <table class="dashboard-new-users-table">
                            <thead>
                                <tr>
                                    <th>Vorname</th>
                                    <th>Nachname</th>
                                    <th>Betrieb</th>
                                    <th>Datum</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr *ngFor="let u of newUsersList">
                                    <td>{{ u.vorname || '–' }}</td>
                                    <td>{{ u.nachname || '–' }}</td>
                                    <td class="dashboard-new-users-betrieb">
                                        <ng-container *ngIf="u.betrieb_bezeichnung || u.vvvo; else noBetrieb">
                                            <span class="dashboard-new-users-betrieb-name">{{
                                                u.betrieb_bezeichnung || '–'
                                            }}</span>
                                            <span class="dashboard-new-users-betrieb-vvvo" *ngIf="u.vvvo">{{
                                                u.vvvo
                                            }}</span>
                                        </ng-container>
                                        <ng-template #noBetrieb>–</ng-template>
                                    </td>
                                    <td>{{ u.erstellt | date: 'dd.MM.yyyy' }}</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </ng-container>
            </div>
        </section>
    </div>
</div>
